import { useState, useEffect, useCallback } from 'react';
import { registerPendingFavorite, setupBackgroundSyncListener, isBackgroundSyncSupported } from '../utils/backgroundSync';
import { onAuthEvent, AUTH_EVENTS } from '../utils/userPreferencesCookie';

const useSharedParkData = () => {
  const [favorites, setFavorites] = useState([]);
  const [favoriteParkIds, setFavoriteParkIds] = useState([]);

  useEffect(() => {
    const readFavoritesFromLocalStorage = () => {
      const savedFavorites = localStorage.getItem('allParksFavorites');
      if (savedFavorites) {
        try {
          setFavorites(JSON.parse(savedFavorites));
        } catch {
          // noop
        }
      } else {
        setFavorites([]);
      }

      const savedParkFavorites = localStorage.getItem('favoriteParkIds');
      if (savedParkFavorites) {
        try {
          const parsed = JSON.parse(savedParkFavorites);
          if (Array.isArray(parsed)) {
            setFavoriteParkIds(parsed);
          } else {
            setFavoriteParkIds([]);
          }
        } catch {
          setFavoriteParkIds([]);
        }
      } else {
        setFavoriteParkIds([]);
      }
    };

    readFavoritesFromLocalStorage();
    
    // Configurer les listeners Background Sync pour les favoris d'attractions
    if (isBackgroundSyncSupported()) {
      setupBackgroundSyncListener((updateData) => {
        if (updateData.tag === 'favorites-sync') {
          const updatedFavorites = localStorage.getItem('allParksFavorites');
          if (updatedFavorites) {
            setTimeout(() => setFavorites(JSON.parse(updatedFavorites)), 0);
          }
        }
      });
    }

    // Synchronisation inter-composants (même onglet) via CustomEvent
    // setTimeout(fn, 0) : différer hors de la phase de rendu React pour éviter
    // "Cannot update a component while rendering a different component"
    const handleFavoritesUpdated = (event) => {
      const detail = event?.detail;
      if (detail && Array.isArray(detail.favorites)) {
        setTimeout(() => setFavorites(detail.favorites), 0);
        return;
      }
      const updatedFavorites = localStorage.getItem('allParksFavorites');
      if (updatedFavorites) {
        setTimeout(() => setFavorites(JSON.parse(updatedFavorites)), 0);
      }
    };

    const handleParkFavoritesUpdated = (event) => {
      const detail = event?.detail;
      if (detail && Array.isArray(detail.favoriteParkIds)) {
        setTimeout(() => setFavoriteParkIds(detail.favoriteParkIds), 0);
        return;
      }
      const updatedParkFavorites = localStorage.getItem('favoriteParkIds');
      if (updatedParkFavorites) {
        try {
          const parsed = JSON.parse(updatedParkFavorites);
          if (Array.isArray(parsed)) {
            setTimeout(() => setFavoriteParkIds(parsed), 0);
          }
        } catch {
          // noop
        }
      }
    };

    window.addEventListener('mpt:favorites-updated', handleFavoritesUpdated);
    window.addEventListener('mpt:park-favorites-updated', handleParkFavoritesUpdated);

    const cleanupSignedIn = onAuthEvent(AUTH_EVENTS.SIGNED_IN, () => {
      readFavoritesFromLocalStorage();
    });

    const cleanupSignedOut = onAuthEvent(AUTH_EVENTS.SIGNED_OUT, () => {
      setFavorites([]);
      setFavoriteParkIds([]);
    });

    return () => {
      window.removeEventListener('mpt:favorites-updated', handleFavoritesUpdated);
      window.removeEventListener('mpt:park-favorites-updated', handleParkFavoritesUpdated);
      cleanupSignedIn();
      cleanupSignedOut();
    };
  }, []);

  const toggleFavorite = useCallback((attractionId, attractionName, parkId) => {
    setFavorites(prev => {
      const favoriteKey = `${parkId}_${attractionId}`;
      const isCurrentlyFavorite = prev.includes(favoriteKey);
      
      const newFavorites = isCurrentlyFavorite
        ? prev.filter(id => id !== favoriteKey)
        : [...prev, favoriteKey];
      
      // Stocker les informations de l'attraction
      const favoritesInfo = JSON.parse(localStorage.getItem('favoritesInfo') || '{}');
      if (!isCurrentlyFavorite) {
        favoritesInfo[favoriteKey] = {
          name: attractionName,
          id: attractionId,
          parkId: parkId
        };
      } else {
        delete favoritesInfo[favoriteKey];
      }
      
      // Sauvegarder localement
      localStorage.setItem('allParksFavorites', JSON.stringify(newFavorites));
      localStorage.setItem('favoritesInfo', JSON.stringify(favoritesInfo));
      
      // Enregistrer pour Background Sync si supporté
      if (isBackgroundSyncSupported()) {
        const action = isCurrentlyFavorite ? 'remove' : 'add';
        registerPendingFavorite(action, attractionId, attractionName, parkId);
        console.log('[useSharedParkData] Favori enregistré pour Background Sync:', action, favoriteKey);
      }
      // Notifier les autres instances du hook dans la même page
      try {
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          const evt = typeof window.CustomEvent === 'function'
            ? new window.CustomEvent('mpt:favorites-updated', { detail: { favorites: newFavorites } })
            : new Event('mpt:favorites-updated');
          window.dispatchEvent(evt);
        }
      } catch {
        // noop
      }
      
      return newFavorites;
    });
  }, []);

  const toggleParkFavorite = useCallback((parkId) => {
    setFavoriteParkIds((prev) => {
      if (!parkId) return prev;
      const isCurrentlyFavorite = prev.includes(parkId);
      const next = isCurrentlyFavorite
        ? prev.filter((id) => id !== parkId)
        : [...prev, parkId];

      localStorage.setItem('favoriteParkIds', JSON.stringify(next));
      try {
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          const evt = typeof window.CustomEvent === 'function'
            ? new window.CustomEvent('mpt:park-favorites-updated', { detail: { favoriteParkIds: next } })
            : new Event('mpt:park-favorites-updated');
          window.dispatchEvent(evt);
        }
      } catch {
        // noop
      }
      return next;
    });
  }, []);

  return { favorites, toggleFavorite, favoriteParkIds, toggleParkFavorite };
};

export default useSharedParkData;
